import { notifications } from '@mantine/notifications';

import {
  createConfigurationTemplateSafe,
  type ConfigurationTemplate,
  updateConfigurationTemplateSafe,
} from '../../../../../services/configurationTemplateManager';

import { type ExperienceSaveDependencies } from './types';
import { resetAndValidate } from '../shared';
import { SessionConfig } from '../../utils/experienceForm/transform';

const updateNotification = (isUpdate: boolean) =>
  notifications.show({
    color: 'green',
    title: 'Successo',
    message: isUpdate
      ? 'Configurazione aggiornata con successo!'
      : 'Configurazione salvata con successo!',
  });

const errorNotification = (message: string) =>
  notifications.show({
    color: 'red',
    title: 'Errore',
    message,
  });

export function createExperienceSaveHandler({
  currentUserId,
  selectedSessionType,
  experienceConfigId,
  experienceForm,
  setExperienceConfigId,
  setIsExperienceFormDirty,
  setIsSavingExperienceConfig,
}: ExperienceSaveDependencies) {
  return async (
    formValues: SessionConfig,
    name: string,
    description: string
  ) => {
    if (!currentUserId) {
      errorNotification('Utente non autenticato');
      return;
    }

    if (selectedSessionType !== 'esperienza') {
      notifications.show({
        color: 'yellow',
        title: 'Attenzione',
        message: 'Seleziona il tipo di sessione Esperienza per procedere',
      });
      return;
    }

    const templateIdFromState = experienceConfigId?.trim();
    const templateId =
      formValues.id?.toString().trim() || templateIdFromState || '';
    const isUpdate = Boolean(templateId);

    setIsSavingExperienceConfig(true);

    try {
      const savedConfiguration = (await (isUpdate
        ? updateConfigurationTemplateSafe(templateId, {
            name,
            description,
            sessionType: selectedSessionType,
            config: formValues,
          })
        : createConfigurationTemplateSafe({
            userId: currentUserId.toString(),
            name,
            description,
            sessionType: selectedSessionType,
            config: formValues,
          }))) as ConfigurationTemplate | null;

      if (!savedConfiguration?.id) {
        throw new Error('Risposta non valida dal server: ID mancante');
      }

      const savedId = savedConfiguration.id.toString();
      setExperienceConfigId(savedId);

      const nextValues: SessionConfig = {
        ...formValues,
        id: savedId,
        name,
        description,
        steps: savedConfiguration.steps ?? formValues.steps,
      };

      await resetAndValidate(experienceForm, nextValues);
      setIsExperienceFormDirty(false);

      updateNotification(isUpdate);
    } catch (error) {
      errorNotification(
        error instanceof Error
          ? error.message
          : 'Errore durante il salvataggio della configurazione'
      );
    } finally {
      setIsSavingExperienceConfig(false);
    }
  };
}
